import Breadcrumbs from "@/components/Breadcrumbs";
import { CommentsService, PageService } from "@/services/admin-services";
import { Page, PageCommentItem } from "@/services/admin-services/models";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import { parseJSON } from "date-fns";
import { orderBy } from "lodash";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Page Comments",
        to: "..",
    },
];

const Details = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [pages, setPages] = useState<Array<Page>>([]);
    const [comments, setComments] = useState<Array<PageCommentItem>>([]);
    const [isUpdating, setIsUpdating] = useState<string>();

    const { slug, language } = useParams();
    const theme = useTheme();

    const commentsService = useMemo(() => new CommentsService("int"), []);
    const pagesService = useMemo(() => new PageService("int"), []);

    useEffect(() => {
        Promise.all([
            commentsService.getForPage(slug!, language!).then(setComments),
            pagesService.getPages().then(setPages),
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!pages || pages.length === 0) return;
        const page = pages.find((x) => x.slug === slug);
        const title = page?.title ?? "Unknown Page";

        setBreadcrumbTrail([...breadcrumbs, { title, to: "" }]);
    }, [pages, slug]);

    const renderStatus = (read: boolean) => {
        if (read) return null;

        const statusColor = theme.palette.warning;

        return (
            <Typography
                variant={"caption"}
                fontWeight={700}
                sx={{
                    bgcolor: alpha(statusColor.light, 0.1),
                    color: statusColor.dark,
                    paddingX: 1.5,
                    paddingY: 0.5,
                    borderRadius: 4,
                    display: "inline",
                }}
            >
                New
            </Typography>
        );
    };

    const onUpdateStatus = (id: string, parentId?: string) => {
        setIsUpdating(`${id}-Read`);

        commentsService.markAsRead(id).then(() => {
            setIsUpdating("");

            const updatedComments = [...comments];
            let updateComment: PageCommentItem;
            if (parentId) {
                const parentIndex = comments.findIndex((c) => c.id === parentId);
                const parent = comments[parentIndex];
                const childIndex = parent.replies.findIndex((c) => c.id === id);
                updateComment = parent.replies[childIndex];
                updateComment.isRead = true;
                updatedComments[parentIndex].replies[childIndex] = updateComment;
            } else {
                const commentIndex = comments.findIndex((c) => c.id === id);
                updateComment = comments[commentIndex];
                updateComment.isRead = true;
                updatedComments[commentIndex] = updateComment;
            }

            setComments(updatedComments);
        });
    };

    const onDelete = (id: string, parentId?: string) => {
        setIsUpdating(`${id}-Delete`);

        commentsService.delete(id).then(() => {
            setIsUpdating("");

            const updatedComments = [...comments];
            if (parentId) {
                const parentIndex = comments.findIndex((c) => c.id === parentId);
                const parent = comments[parentIndex];
                const childIndex = parent.replies.findIndex((c) => c.id === id);
                updatedComments[parentIndex].replies.splice(childIndex, 1);
            } else {
                const commentIndex = comments.findIndex((c) => c.id === id);
                updatedComments.splice(commentIndex, 1);
            }

            setComments(updatedComments);
        });
    };

    const buildSlug = (page: Page) => {
        let result = page.slug;

        if (page.parentPageId) {
            const parent = pages.find((x) => x.id === page.parentPageId)!;
            result = `${buildSlug(parent)}/${result}`;
        }

        return result;
    };

    const getSlug = (pageSlug: string) => {
        if (!pages || pages.length === 0) return pageSlug;

        const page = pages.find((x) => x.slug === pageSlug)!;
        return buildSlug(page);
    };

    const renderComment = (key: number, item: PageCommentItem, parentId?: string) => {
        return (
            <Fragment key={key}>
                <TableRow
                    key={key}
                    sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        verticalAlign: "top",
                    }}
                >
                    <TableCell
                        component="th"
                        scope="row"
                        sx={{
                            background: item.isAdmin
                                ? "linear-gradient(90deg, #05B692 10px, transparent 0%);"
                                : "unset",
                        }}
                    >
                        <Typography
                            color={"text.secondary"}
                            variant={"body2"}
                            sx={{ whiteSpace: "pre-wrap", ml: parentId ? 4 : "unset" }}
                        >
                            {item.commentBlob}
                        </Typography>
                    </TableCell>
                    <TableCell component="td" scope="row">
                        <Typography color={"text.secondary"} variant={"body2"}>
                            {item.username} ({item.email})
                        </Typography>
                    </TableCell>
                    <TableCell component="td" scope="row">
                        <Typography color={"text.secondary"} variant={"body2"}>
                            {parseJSON(item.commentDate).toLocaleString("nl-be") || "-"} {renderStatus(item.isRead)}
                        </Typography>
                    </TableCell>
                    <TableCell scope="row" sx={{ p: 0 }}>
                        <Box sx={{ display: "flex", m: 0.5 }}>
                            <Box sx={{ ml: "auto", mr: 1, position: "relative" }}>
                                <Button
                                    color={"primary"}
                                    onClick={() => onUpdateStatus(item.id, parentId)}
                                    disabled={Boolean(isUpdating) || item.isRead}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    Mark as read
                                </Button>
                                {isUpdating === `${item.id}-Read` && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ mr: 1, position: "relative" }}>
                                <Button
                                    color={"error"}
                                    onClick={() => onDelete(item.id, parentId)}
                                    disabled={Boolean(isUpdating)}
                                >
                                    Delete
                                </Button>
                                {isUpdating === `${item.id}-Delete` && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ ml: "auto", position: "relative" }}>
                                <Button
                                    color={"info"}
                                    onClick={() => onUpdateStatus(item.id, parentId)}
                                    sx={{ whiteSpace: "nowrap" }}
                                    href={`/${item.languageCode.replace("int", "be")}/${getSlug(slug!)}`}
                                    target={"_blank"}
                                >
                                    View
                                </Button>
                            </Box>
                        </Box>
                    </TableCell>
                </TableRow>

                {item.replies?.map((reply, i) => renderComment(i, reply, item.id))}
            </Fragment>
        );
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Divider />

            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", flexDirection: "column" }}>
                    <Box sx={{ ml: "auto", position: "relative" }}>
                        <Button
                            variant="contained"
                            component="a"
                            startIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                            href={getSlug(slug!)}
                            target="_blank"
                            disabled={isLoading}
                        >
                            View page
                        </Button>
                        {isLoading && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    marginTop: "-12px",
                                    marginLeft: "-12px",
                                }}
                            />
                        )}
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 500 }}>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Comment
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Posted by
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Posted on
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading || !comments ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : comments.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 200,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ textAlign: "center" }}>
                                                No comments available for this element.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    orderBy(comments, "commentDate", "desc").map((item, i) => renderComment(i, item))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Details;
